import {Column, ColumnSizingState, flexRender, Row, RowData} from "@tanstack/react-table";
import {
    ADDITIONAL_NOTES_COLUMN_ID,
    ADDITIONAL_NOTES_COLUMN_WIDTH,
    MIN_PRINT_COLUMN_WIDTH,
    PRINT_CONFIG_STORAGE_PREFIX,
    PRINT_PAGE_PADDING,
    PRINT_PAGE_SIZES
} from "./constants";
import {
    ServerTablePrintColumn,
    ServerTablePrintOrientation,
    ServerTablePrintPageSlice,
    ServerTablePrintPersistedConfig,
    ServerTablePrintRenderMode,
    ServerTablePrintTextSize
} from "./types";

export const getPrintContentWidth = (orientation: ServerTablePrintOrientation) =>
    PRINT_PAGE_SIZES[orientation].width - 2 * PRINT_PAGE_PADDING;

export const hasPrintColumnBelowMinimumWidth = (
    columns: readonly {width: number}[],
    contentWidth: number
) => {
    if (columns.length === 0) return false;
    const totalWidth = columns.reduce((sum, column) => sum + column.width, 0);
    if (totalWidth <= 0) return true;

    return columns.some(column =>
        (column.width / totalWidth) * contentWidth < MIN_PRINT_COLUMN_WIDTH
    );
};

const formatPrintValue = (
    value: unknown,
    locale: string,
    translate: (value: string) => string
): string => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toLocaleDateString(locale);
    if (Array.isArray(value)) {
        return value
            .map(entry => formatPrintValue(entry, locale, translate))
            .filter(Boolean)
            .join(', ');
    }
    if (typeof value === 'boolean') return translate(value ? 'Yes' : 'No');
    if (typeof value === 'object') return '';
    return String(value);
};

export const getServerTablePrintColumns = <TData extends RowData>(
    leafColumns: readonly Column<TData, unknown>[],
    columnSizing: ColumnSizingState,
    translate: (value: string) => string
): ServerTablePrintColumn<TData>[] => {
    const tableColumns: ServerTablePrintColumn<TData>[] = leafColumns
        .filter(column => (
            column.columnDef.meta?.printable === true
            && column.id !== ADDITIONAL_NOTES_COLUMN_ID
        ))
        .map((column): ServerTablePrintColumn<TData> => {
            const {columnDef} = column;
            const header = columnDef.header;

            return {
                id: column.id,
                label: typeof header === 'string'
                    ? translate(header)
                    : columnDef.meta?.headerLabel ?? column.id,
                width: columnDef.meta?.printWidth ?? columnSizing[column.id] ?? column.getSize(),
                defaultSelected: columnDef.meta?.printDefault,
                render: (row: Row<TData>, context: {locale: string}) => {
                    if (columnDef.meta?.printCell) {
                        return columnDef.meta.printCell(row, context);
                    }

                    return formatPrintValue(row.getValue(column.id), context.locale, translate);
                },
                renderOriginal: (row: Row<TData>, context: {locale: string}) => {
                    if (columnDef.meta?.printOriginalCell) {
                        return columnDef.meta.printOriginalCell(row, context);
                    }

                    const cell = row.getAllCells().find(item => item.column.id === column.id);
                    return cell ? flexRender(columnDef.cell, cell.getContext()) : null;
                }
            };
        });

    // The reserved Additional notes column is print-only, blank, and never selected by default.
    return [...tableColumns, {
        id: ADDITIONAL_NOTES_COLUMN_ID,
        label: translate('Additional notes'),
        width: ADDITIONAL_NOTES_COLUMN_WIDTH,
        defaultSelected: false,
        render: () => null
    }];
};

export const getDefaultServerTablePrintColumnIds = <TData extends RowData>(
    columns: readonly ServerTablePrintColumn<TData>[]
) => {
    const explicitDefaults = columns.filter(column => column.defaultSelected === true);
    const defaultColumns = explicitDefaults.length
        ? explicitDefaults
        : columns.filter(column => column.defaultSelected !== false);

    return defaultColumns.map(column => column.id);
};

export const movePrintColumn = (
    columnIds: readonly string[],
    activeId: string,
    overId: string
): string[] => {
    const from = columnIds.indexOf(activeId);
    const to = columnIds.indexOf(overId);
    const next = [...columnIds];
    if (from === -1 || to === -1 || from === to) return next;
    next.splice(to, 0, ...next.splice(from, 1));
    return next;
};

export const resizePrintColumnsAcrossBoundary = (
    widths: Record<string, number>,
    columnIds: readonly string[],
    leftId: string,
    rightId: string,
    delta: number,
    minWidth: number
): Record<string, number> => {
    const leftIndex = columnIds.indexOf(leftId);
    const rightIndex = columnIds.indexOf(rightId);
    if (
        delta === 0
        || leftIndex === -1
        || rightIndex !== leftIndex + 1
        || widths[leftId] === undefined
        || widths[rightId] === undefined
    ) {
        return widths;
    }

    const next = {...widths};
    const donorIndexes = delta < 0
        ? Array.from({length: leftIndex + 1}, (_, index) => leftIndex - index)
        : Array.from({length: columnIds.length - rightIndex}, (_, index) => rightIndex + index);
    let remaining = Math.abs(delta);
    let transferred = 0;

    for (const donorIndex of donorIndexes) {
        if (remaining <= 0) break;
        const donorId = columnIds[donorIndex];
        const donorWidth = next[donorId];
        if (donorWidth === undefined) continue;

        const available = Math.max(0, donorWidth - minWidth);
        const taken = Math.min(available, remaining);
        next[donorId] = donorWidth - taken;
        transferred += taken;
        remaining -= taken;
    }

    const growingId = delta < 0 ? rightId : leftId;
    next[growingId] += transferred;
    return next;
};

export const readServerTablePrintConfig = (tableId: string): ServerTablePrintPersistedConfig | null => {
    try {
        const raw = localStorage.getItem(`${PRINT_CONFIG_STORAGE_PREFIX}${tableId}`);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<ServerTablePrintPersistedConfig>;
        const orientationValid = parsed.orientation === 'portrait' || parsed.orientation === 'landscape';
        const textSizeValid = parsed.textSize === 'compact' || parsed.textSize === 'standard' || parsed.textSize === 'large';
        const renderModeValid = parsed.renderMode === undefined || parsed.renderMode === 'raw' || parsed.renderMode === 'original';
        const columnIdsValid = Array.isArray(parsed.columnIds) && parsed.columnIds.every(id => typeof id === 'string');
        if (!orientationValid || !textSizeValid || !renderModeValid || !columnIdsValid) return null;

        return {
            orientation: parsed.orientation as ServerTablePrintOrientation,
            textSize: parsed.textSize as ServerTablePrintTextSize,
            renderMode: (parsed.renderMode ?? 'raw') as ServerTablePrintRenderMode,
            columnIds: Array.from(new Set(parsed.columnIds as string[])),
            columnWidths: Object.fromEntries(
                Object.entries(parsed.columnWidths ?? {})
                    .filter(([, width]) => typeof width === 'number' && Number.isFinite(width) && width > 0)
            )
        };
    } catch {
        return null;
    }
};

export const writeServerTablePrintConfig = (tableId: string, config: ServerTablePrintPersistedConfig) => {
    try {
        localStorage.setItem(`${PRINT_CONFIG_STORAGE_PREFIX}${tableId}`, JSON.stringify(config));
    } catch {
        // Printing remains available when browser storage is unavailable.
    }
};

export const clearServerTablePrintConfig = (tableId: string) => {
    try {
        localStorage.removeItem(`${PRINT_CONFIG_STORAGE_PREFIX}${tableId}`);
    } catch {
        // Reset remains safe when browser storage is unavailable.
    }
};

export const packRowsIntoPages = (
    rowHeights: readonly number[],
    firstPageBudget: number,
    nextPageBudget: number
): ServerTablePrintPageSlice[] => {
    if (rowHeights.length === 0) return [{start: 0, end: 0}];

    const pages: ServerTablePrintPageSlice[] = [];
    let start = 0;
    let used = 0;

    rowHeights.forEach((height, index) => {
        const budget = pages.length === 0 ? firstPageBudget : nextPageBudget;
        if (index > start && used + height > budget) {
            pages.push({start, end: index});
            start = index;
            used = 0;
        }
        used += height;
    });

    pages.push({start, end: rowHeights.length});
    return pages;
};

export const countOversizedPrintRows = (
    rowHeights: readonly number[],
    pages: readonly ServerTablePrintPageSlice[],
    firstPageBudget: number,
    nextPageBudget: number
) => pages.reduce((count, page, pageIndex) => {
    const budget = pageIndex === 0 ? firstPageBudget : nextPageBudget;
    return count + rowHeights
        .slice(page.start, page.end)
        .filter(height => height > budget)
        .length;
}, 0);
